import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";

import { getChoferes } from "../helpers/rutaChoferes";
import { getViajes } from "../helpers/rutaViajes";
import { getPagochoferes, postPagochofer } from "../helpers/rutaPagochofer";
import { getCajas } from "../helpers/rutaCajas";

/* =========================
   FECHAS / FORMATOS
========================= */

const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const firstDayOfMonthISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  local.setDate(1);
  return local.toISOString().slice(0, 10);
};

const nowLocalDateTimeInput = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const num = (v) => Number(v || 0) || 0;

const numInput = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return 0;

  const normalizado = s.includes(",")
    ? s.replace(/\./g, "").replace(",", ".")
    : s;

  return Number(normalizado) || 0;
};

const money = (n) =>
  num(n).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const kmFmt = (n) =>
  num(n).toLocaleString("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const getDateValue = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (v?.$date) return v.$date;
  return v;
};

const dateOnly = (value) => {
  const raw = getDateValue(value);
  if (!raw) return "";

  const s = String(raw).trim();

  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";

  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

/* =========================
   UTILS
========================= */

const firstPresent = (...values) => {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
};

const getId = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (v?.$oid) return v.$oid;
  if (typeof v?._id === "string") return v._id;
  if (v?._id?.$oid) return v._id.$oid;
  if (typeof v?.id === "string") return v.id;
  return "";
};

const getChoferNombreBase = (c) => {
  if (!c) return "";

  return (
    c.chofer ||
    c.nombreyapellido ||
    [c.nombre, c.apellido].filter(Boolean).join(" ").trim() ||
    c.nombre ||
    c.razonsocial ||
    c.descripcion ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();
};

const getChoferLabel = (c) => {
  const nombre = getChoferNombreBase(c);
  return c?.codcho ? `${nombre} - ${c.codcho}` : nombre;
};

const getChoferIdFromViaje = (v) => {
  return getId(v?.choferId) || getId(v?.chofer);
};

const getFechaViaje = (v) => {
  return (
    firstPresent(
      v?.tramos?.[0]?.fecha,
      v?.fecha,
      v?.fechaviaje,
      v?.fechaViaje,
      v?.fechainicio,
      v?.fechaInicio,
      v?.createdAt
    ) || ""
  );
};

const getNroViaje = (v) => {
  return firstPresent(v?.nroViaje, v?.nroviaje) ?? "";
};

const nroViajeSortable = (v) => {
  const n = String(getNroViaje(v) || "").replace(",", ".");
  return Number(n) || 0;
};

const getTotalKmDesdeCombustible = (v) => {
  const filas = Array.isArray(v?.combustible) ? v.combustible : [];

  const kms = filas
    .map((item) =>
      num(
        firstPresent(
          item?.kmTablero,
          item?.kmtablero,
          item?.km,
          item?.odometro,
          item?.odómetro
        )
      )
    )
    .filter((n) => n > 0);

  if (kms.length < 2) return 0;

  return Math.max(...kms) - Math.min(...kms);
};

const getTotalKm = (v) => {
  const directo = num(
    firstPresent(
      v?.totalKm,
      v?.totalkm,
      v?.kmtotal,
      v?.kmTotal,
      v?.totalkms,
      v?.kmRecorridos,
      v?.resumen?.totalKm,
      v?.resumenFinal?.totalKm,
      v?.resumenfinal?.totalKm
    )
  );

  if (directo) return directo;

  return getTotalKmDesdeCombustible(v);
};

const getValorKm = (v) =>
  num(
    firstPresent(
      v?.tarifaPorKm,
      v?.tarifaporkm,
      v?.valorKm,
      v?.valorkm,
      v?.valorPorKm,
      v?.valorporkm,
      v?.precioKm,
      v?.preciokm,
      v?.tarifa,
      v?.resumen?.valorKm,
      v?.resumenFinal?.valorKm,
      v?.resumenfinal?.valorKm
    )
  );

const sumByKeys = (arr, keys = ["monto"]) => {
  if (!Array.isArray(arr)) return 0;

  return arr.reduce((acc, item) => {
    const valor =
      keys.reduce((found, key) => found ?? item?.[key], undefined) ?? 0;

    return acc + num(valor);
  }, 0);
};

const getTotalViaticos = (v) => {
  const directo = firstPresent(
    v?.totalViaticos,
    v?.resumen?.viaticos,
    v?.resumenFinal?.viaticos,
    v?.resumenfinal?.viaticos
  );

  if (directo !== null && directo !== undefined && directo !== "") {
    return num(directo);
  }

  return sumByKeys(v?.viaticos, ["monto", "importe", "total"]);
};

const getTotalGastos = (v) => {
  const directo = firstPresent(
    v?.totalGastos,
    v?.resumen?.gastos,
    v?.resumenFinal?.gastos,
    v?.resumenfinal?.gastos
  );

  if (directo !== null && directo !== undefined && directo !== "") {
    return num(directo);
  }

  return sumByKeys(v?.gastos, ["monto", "importe", "total"]);
};

const getTotalAdelantos = (v) => {
  const directo = firstPresent(
    v?.totalAdelantos,
    v?.resumen?.adelantos,
    v?.resumenFinal?.adelantos,
    v?.resumenfinal?.adelantos
  );

  if (directo !== null && directo !== undefined && directo !== "") {
    return num(directo);
  }

  return sumByKeys(v?.adelantos, ["monto", "importe", "total"]);
};

const getValorViaje = (v) => {
  const km = getTotalKm(v);
  const valorKm = getValorKm(v);
  const calculado = km * valorKm;

  if (calculado) return calculado;

  return num(
    firstPresent(
      v?.valorViaje,
      v?.valorviaje,
      v?.totalViaje,
      v?.totalviaje,
      v?.resumen?.valorViaje,
      v?.resumenFinal?.valorViaje,
      v?.resumenfinal?.valorViaje
    )
  );
};

const getNetoViaje = (v) => {
  return (
    getValorViaje(v) +
    getTotalViaticos(v) +
    getTotalGastos(v) -
    getTotalAdelantos(v)
  );
};

const getAppViajeId = (app) => {
  if (!app) return "";
  return getId(app?.viaje);
};

/* =========================
   NORMALIZADORES
========================= */

const normalizarChoferes = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.choferes)) return resp.choferes;
  if (Array.isArray(resp.chofer)) return resp.chofer;
  if (Array.isArray(resp.data?.choferes)) return resp.data.choferes;
  if (Array.isArray(resp.data?.chofer)) return resp.data.chofer;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarViajes = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.viajes)) return resp.viajes;
  if (Array.isArray(resp.viaje)) return resp.viaje;
  if (Array.isArray(resp.data?.viajes)) return resp.data.viajes;
  if (Array.isArray(resp.data?.viaje)) return resp.data.viaje;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarPagos = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.pagos)) return resp.pagos;
  if (Array.isArray(resp.pago)) return resp.pago;
  if (Array.isArray(resp.data?.pagos)) return resp.data.pagos;
  if (Array.isArray(resp.data?.pago)) return resp.data.pago;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarCajas = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.cajas)) return resp.cajas;
  if (Array.isArray(resp.caja)) return resp.caja;
  if (Array.isArray(resp.data?.cajas)) return resp.data.cajas;
  if (Array.isArray(resp.data?.caja)) return resp.data.caja;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

/* =========================
   COMPONENTE
========================= */

export default function ModalPagochofer({
  show,
  setShow,
  onGuardado,
  choferInicial = "",
  desdeInicial = firstDayOfMonthISO(),
  hastaInicial = todayLocalISO(),
  saldoCtaCteInicial = 0,
}) {
  const mountedRef = useRef(false);

  const [choferes, setChoferes] = useState([]);
  const [viajes, setViajes] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [cajas, setCajas] = useState([]);

  const [cargandoChoferes, setCargandoChoferes] = useState(false);
  const [cargandoViajes, setCargandoViajes] = useState(false);
  const [cargandoCajas, setCargandoCajas] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [formValues, setFormValues] = useState({
    chofer: "",
    desde: firstDayOfMonthISO(),
    hasta: todayLocalISO(),
    fechapago: nowLocalDateTimeInput(),
    mediopago: "EFECTIVO",
    observaciones: "",
    caja: "",
  });

  const [seleccion, setSeleccion] = useState({});
  const [montoAPagar, setMontoAPagar] = useState("");

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!show || !mountedRef.current) return;

    const saldoPantalla = num(saldoCtaCteInicial);

    setFormValues({
      chofer: choferInicial || "",
      desde: desdeInicial || firstDayOfMonthISO(),
      hasta: hastaInicial || todayLocalISO(),
      fechapago: nowLocalDateTimeInput(),
      mediopago: "EFECTIVO",
      observaciones: "",
      caja: "",
    });

    setMontoAPagar(saldoPantalla > 0 ? String(saldoPantalla.toFixed(2)) : "");
    setViajes([]);
    setPagos([]);
    setSeleccion({});
    setError("");
  }, [show, choferInicial, desdeInicial, hastaInicial, saldoCtaCteInicial]);

  const resetForm = () => {
    if (!mountedRef.current) return;

    setViajes([]);
    setPagos([]);
    setSeleccion({});
    setMontoAPagar("");
    setError("");

    setFormValues({
      chofer: "",
      desde: firstDayOfMonthISO(),
      hasta: todayLocalISO(),
      fechapago: nowLocalDateTimeInput(),
      mediopago: "EFECTIVO",
      observaciones: "",
      caja: "",
    });
  };

  const handleClose = () => {
    resetForm();
    setShow(false);
  };

  useEffect(() => {
    if (!show) return;

    let activo = true;

    const cargarInicial = async () => {
      if (activo && mountedRef.current) {
        setCargandoChoferes(true);
        setCargandoCajas(true);
        setError("");
      }

      try {
        const [respChoferes, respCajas] = await Promise.all([
          getChoferes(),
          getCajas(),
        ]);

        if (activo && mountedRef.current) {
          setChoferes(normalizarChoferes(respChoferes));

          setCajas(
            normalizarCajas(respCajas)
              .filter((c) => c?.activo !== false)
              .sort((a, b) =>
                String(a?.descripcion || "").localeCompare(
                  String(b?.descripcion || ""),
                  "es",
                  { sensitivity: "base" }
                )
              )
          );
        }
      } catch (e) {
        console.error("Error al cargar datos iniciales:", e?.response?.data || e);

        if (activo && mountedRef.current) {
          setError("No se pudieron cargar choferes/cajas");
        }
      } finally {
        if (activo && mountedRef.current) {
          setCargandoChoferes(false);
          setCargandoCajas(false);
        }
      }
    };

    cargarInicial();

    return () => {
      activo = false;
    };
  }, [show]);

  const handleChange = ({ target }) => {
    const { name, value } = target;

    if (!mountedRef.current) return;

    setFormValues((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === "mediopago" && value !== "EFECTIVO") {
        next.caja = "";
      }

      return next;
    });
  };

  const cajaSeleccionada = useMemo(() => {
    return cajas.find((c) => String(c._id) === String(formValues.caja)) || null;
  }, [cajas, formValues.caja]);

  const cargarViajes = async () => {
    if (!formValues.chofer) {
      setError("Seleccioná un chofer");
      return;
    }

    if (mountedRef.current) {
      setCargandoViajes(true);
      setError("");
      setViajes([]);
      setPagos([]);
      setSeleccion({});
    }

    try {
      const [respViajes, respPagos] = await Promise.all([
        getViajes(),
        getPagochoferes({ chofer: formValues.chofer }),
      ]);

      const todosLosViajes = normalizarViajes(respViajes);

      if (respPagos?.ok === false) {
        if (mountedRef.current) {
          setError(respPagos?.error || "No se pudieron cargar los pagos");
        }
        return;
      }

      const todosLosPagos = normalizarPagos(respPagos);

      const desde = formValues.desde || "";
      const hasta = formValues.hasta || "";

      const viajesFiltrados = todosLosViajes
        .filter((v) => {
          const activo = v?.estado ? v.estado === "activo" : v?.activo !== false;
          const choferOk = getChoferIdFromViaje(v) === formValues.chofer;

          const fecha = dateOnly(getFechaViaje(v));
          const fechaOk =
            (!desde || fecha >= desde) && (!hasta || fecha <= hasta);

          return activo && choferOk && fechaOk;
        })
        .sort((a, b) => {
          const fa = dateOnly(getFechaViaje(a));
          const fb = dateOnly(getFechaViaje(b));

          if (fa < fb) return -1;
          if (fa > fb) return 1;

          return nroViajeSortable(a) - nroViajeSortable(b);
        });

      const pagosActivos = todosLosPagos.filter((p) =>
        p?.estado ? p.estado === "activo" : p?.activo !== false
      );

      if (mountedRef.current) {
        setViajes(viajesFiltrados);
        setPagos(pagosActivos);
      }
    } catch (e) {
      console.error("Error al cargar viajes/pagos:", e?.response?.data || e);

      if (mountedRef.current) {
        setError("Ocurrió un error al cargar viajes y pagos");
      }
    } finally {
      if (mountedRef.current) {
        setCargandoViajes(false);
      }
    }
  };

  const viajesCalculados = useMemo(() => {
    return viajes.map((v) => {
      const _id = getId(v) || v?._id || v?.id;

      const valorviaje = getValorViaje(v);
      const totalviaticos = getTotalViaticos(v);
      const totalgastos = getTotalGastos(v);
      const totaladelantos = getTotalAdelantos(v);
      const neto = getNetoViaje(v);

      const aplicadoprevio = pagos.reduce((acc, pago) => {
        const sumaApps = Array.isArray(pago?.aplicaciones)
          ? pago.aplicaciones.reduce((sub, app) => {
              const viajeId = getAppViajeId(app);

              return String(viajeId) === String(_id)
                ? sub + num(app?.montoaplicado)
                : sub;
            }, 0)
          : 0;

        return acc + sumaApps;
      }, 0);

      const saldopendiente = neto - aplicadoprevio;

      return {
        ...v,
        _id,
        fecha: dateOnly(getFechaViaje(v)),
        nroviaje: getNroViaje(v),
        totalkm: getTotalKm(v),
        valorkm: getValorKm(v),
        valorviaje,
        totalviaticos,
        totalgastos,
        totaladelantos,
        neto,
        aplicadoprevio,
        saldopendiente,
      };
    });
  }, [viajes, pagos]);

  const aplicacionesSeleccionadas = useMemo(() => {
    return Object.values(seleccion).filter((x) => num(x?.montoaplicado) > 0);
  }, [seleccion]);

  const montototal = useMemo(() => {
    return aplicacionesSeleccionadas.reduce(
      (acc, item) => acc + num(item.montoaplicado),
      0
    );
  }, [aplicacionesSeleccionadas]);

  const resumen = useMemo(() => {
    return viajesCalculados.reduce(
      (acc, v) => {
        acc.km += num(v.totalkm);
        acc.valorviaje += num(v.valorviaje);
        acc.viaticos += num(v.totalviaticos);
        acc.gastos += num(v.totalgastos);
        acc.adelantos += num(v.totaladelantos);
        acc.neto += num(v.neto);
        acc.aplicadoprevio += num(v.aplicadoprevio);
        acc.saldopendiente += num(v.saldopendiente);

        return acc;
      },
      {
        km: 0,
        valorviaje: 0,
        viaticos: 0,
        gastos: 0,
        adelantos: 0,
        neto: 0,
        aplicadoprevio: 0,
        saldopendiente: 0,
      }
    );
  }, [viajesCalculados]);

  const saldoRealCuenta = useMemo(() => {
    const saldoApp = num(saldoCtaCteInicial);

    return saldoApp > 0 ? saldoApp : resumen.saldopendiente;
  }, [saldoCtaCteInicial, resumen.saldopendiente]);

  const diferenciaAdelantosExternos = useMemo(() => {
    return resumen.saldopendiente - saldoRealCuenta;
  }, [resumen.saldopendiente, saldoRealCuenta]);

  const saldoMaximoPagable = useMemo(() => {
    const saldoReal = num(saldoRealCuenta);

    return saldoReal > 0 ? saldoReal : num(resumen.saldopendiente);
  }, [saldoRealCuenta, resumen.saldopendiente]);

  const montoObjetivoPago = useMemo(() => {
    const escrito = numInput(montoAPagar);

    if (escrito > 0) {
      return Math.min(escrito, saldoMaximoPagable);
    }

    return saldoMaximoPagable;
  }, [montoAPagar, saldoMaximoPagable]);

  const saldoDisponibleViajes = useMemo(() => {
    return viajesCalculados.reduce((acc, v) => {
      return acc + Math.max(0, num(v.saldopendiente));
    }, 0);
  }, [viajesCalculados]);

  const permitePagoACuenta = useMemo(() => {
    return (
      aplicacionesSeleccionadas.length === 0 &&
      num(montoObjetivoPago) > 0 &&
      num(saldoMaximoPagable) > 0 &&
      num(montoObjetivoPago) <= num(saldoMaximoPagable) + 0.01
    );
  }, [
    aplicacionesSeleccionadas.length,
    montoObjetivoPago,
    saldoMaximoPagable,
  ]);

  const montoTotalOperacion = useMemo(() => {
    if (montototal > 0) return montototal;

    if (permitePagoACuenta) {
      return montoObjetivoPago;
    }

    return 0;
  }, [montototal, permitePagoACuenta, montoObjetivoPago]);

  const totalSeleccionadoEn = (obj, exceptKey = "") => {
    return Object.entries(obj || {}).reduce((acc, [key, item]) => {
      if (String(key) === String(exceptKey)) return acc;
      return acc + num(item?.montoaplicado);
    }, 0);
  };

  const aplicarMontoExacto = (montoBase) => {
    const montoObjetivo = Math.min(num(montoBase), saldoMaximoPagable);

    if (montoObjetivo <= 0) {
      setError("Ingresá un monto a pagar mayor a 0");
      return;
    }

    if (viajesCalculados.length === 0) {
      setError("Primero buscá los viajes del chofer");
      return;
    }

    // Si el saldo real a pagar supera lo aplicable a viajes,
    // se paga como pago a cuenta de la cuenta corriente del chofer.
    if (montoObjetivo > saldoDisponibleViajes + 0.01 && saldoMaximoPagable > 0) {
      setSeleccion({});
      setError("");
      return;
    }

    let restante = Number(montoObjetivo.toFixed(2));
    const nuevaSeleccion = {};

    for (const viaje of viajesCalculados) {
      if (restante <= 0) break;

      const saldoViaje = Math.max(0, num(viaje.saldopendiente));
      if (saldoViaje <= 0.01) continue;

      const montoAplicado = Math.min(saldoViaje, restante);

      nuevaSeleccion[viaje._id] = {
        viaje: viaje._id,
        nroviaje: viaje.nroviaje,
        montoaplicado: Number(montoAplicado.toFixed(2)),
      };

      restante = Number((restante - montoAplicado).toFixed(2));
    }

    setSeleccion(nuevaSeleccion);

    if (restante > 0.01) {
      setError(
        `El monto ingresado supera el saldo pendiente de los viajes. Quedó sin aplicar: $ ${money(
          restante
        )}`
      );
    } else {
      setError("");
    }
  };

  const aplicarMontoAPagar = () => {
    aplicarMontoExacto(montoObjetivoPago);
  };

  const aplicarSaldoReal = () => {
    const monto = Number(saldoMaximoPagable.toFixed(2));

    setMontoAPagar(String(monto.toFixed(2)));
    aplicarMontoExacto(monto);
  };

  const handleToggleViaje = (viaje) => {
    const key = viaje._id;

    setSeleccion((prev) => {
      const copia = { ...prev };

      if (copia[key]) {
        delete copia[key];
        setError("");
        return copia;
      }

      if (viaje.saldopendiente <= 0) return prev;

      const totalActual = totalSeleccionadoEn(copia);
      const disponible = Number((montoObjetivoPago - totalActual).toFixed(2));

      if (disponible <= 0) {
        setError(`Ya llegaste al monto a pagar: $ ${money(montoObjetivoPago)}`);
        return prev;
      }

      const montoAplicado = Math.min(num(viaje.saldopendiente), disponible);

      copia[key] = {
        viaje: viaje._id,
        nroviaje: viaje.nroviaje,
        montoaplicado: Number(montoAplicado.toFixed(2)),
      };

      setError("");
      return copia;
    });
  };

  const handleMontoAplicado = (viaje, value) => {
    let monto = numInput(value);

    setSeleccion((prev) => {
      const copia = { ...prev };

      const totalOtros = totalSeleccionadoEn(copia, viaje._id);
      const disponibleParaEste = Number(
        (montoObjetivoPago - totalOtros).toFixed(2)
      );

      if (disponibleParaEste <= 0) {
        delete copia[viaje._id];
        setError(`Ya llegaste al monto a pagar: $ ${money(montoObjetivoPago)}`);
        return copia;
      }

      if (monto < 0) monto = 0;
      if (monto > viaje.saldopendiente) monto = viaje.saldopendiente;
      if (monto > disponibleParaEste) monto = disponibleParaEste;

      if (monto <= 0) {
        delete copia[viaje._id];
        return copia;
      }

      copia[viaje._id] = {
        viaje: viaje._id,
        nroviaje: viaje.nroviaje,
        montoaplicado: Number(monto.toFixed(2)),
      };

      setError("");
      return copia;
    });
  };

  const errorToText = (err) => {
    if (!err) return "Ocurrió un error";
    if (typeof err === "string") return err;
    if (typeof err?.error === "string") return err.error;
    if (typeof err?.msg === "string") return err.msg;
    if (typeof err?.message === "string") return err.message;

    if (err?.detalles) {
      try {
        return JSON.stringify(err.detalles);
      } catch (_) {}
    }

    try {
      return JSON.stringify(err);
    } catch (_) {
      return "Ocurrió un error";
    }
  };

  const detalleViajesPagoCuenta = useMemo(() => {
    if (!permitePagoACuenta) return "";

    const viajesConSaldo = viajesCalculados.filter(
      (v) => Math.abs(num(v.saldopendiente)) > 0.01
    );

    if (viajesConSaldo.length === 0) return "";

    const detalle = viajesConSaldo
      .map((v) => {
        return `Viaje ${v.nroviaje}: $ ${money(v.saldopendiente)}`;
      })
      .join(" | ");

    return `Viajes considerados: ${detalle}`;
  }, [permitePagoACuenta, viajesCalculados]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.chofer) {
      setError("Seleccioná un chofer");
      return;
    }

    if (aplicacionesSeleccionadas.length === 0 && !permitePagoACuenta) {
      setError("Seleccioná al menos un viaje con monto aplicado");
      return;
    }

    if (montoTotalOperacion <= 0) {
      setError("El monto total debe ser mayor a 0");
      return;
    }

    if (montoTotalOperacion > saldoMaximoPagable + 0.01) {
      setError(
        `El pago no puede superar el saldo real a pagar: $ ${money(
          saldoMaximoPagable
        )}`
      );
      return;
    }

    if (formValues.mediopago === "EFECTIVO") {
      if (!formValues.caja) {
        setError("Seleccioná la caja desde donde sale el dinero");
        return;
      }

      if (num(cajaSeleccionada?.saldoActual) < montoTotalOperacion) {
        setError(
          `Saldo insuficiente en la caja seleccionada. Disponible: $ ${money(
            cajaSeleccionada?.saldoActual
          )}`
        );
        return;
      }
    }

    const payload = {
      chofer: formValues.chofer,
      fechapago: formValues.fechapago
        ? new Date(formValues.fechapago).toISOString()
        : null,
      montototal: montoTotalOperacion,
      mediopago: formValues.mediopago,
      observaciones: permitePagoACuenta
        ? [
            formValues.observaciones,
            "Pago a cuenta de saldo de cuenta corriente sin aplicar a viaje",
            detalleViajesPagoCuenta,
          ]
            .filter(Boolean)
            .join(" | ")
        : formValues.observaciones || "",
      aplicaciones: aplicacionesSeleccionadas,
      caja: formValues.mediopago === "EFECTIVO" ? formValues.caja : null,

      // clave para el backend
      pagoACuenta: permitePagoACuenta,
    };

    if (mountedRef.current) {
      setGuardando(true);
      setError("");
    }

    try {
      const resp = await postPagochofer(payload);

      if (resp?.ok === false) {
        if (mountedRef.current) {
          setError(errorToText(resp));
        }
        return;
      }

      if (onGuardado) {
        await onGuardado(resp);
      }

      handleClose();
    } catch (e) {
      console.error("Error al guardar pago:", e?.response?.data || e);

      const detalle = e?.response?.data?.detalles
        ? JSON.stringify(e.response.data.detalles)
        : null;

      if (mountedRef.current) {
        setError(
          errorToText(e?.response?.data) ||
            detalle ||
            e?.response?.data?.message ||
            "Ocurrió un error al guardar el pago"
        );
      }
    } finally {
      if (mountedRef.current) {
        setGuardando(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Pago a chofer</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Chofer</Form.Label>
                <Form.Select
                  name="chofer"
                  value={formValues.chofer}
                  onChange={handleChange}
                  disabled={cargandoChoferes || guardando}
                >
                  <option value="">Seleccione chofer</option>

                  {choferes
                    .filter((c) => c?.activo !== false)
                    .sort((a, b) =>
                      getChoferNombreBase(a).localeCompare(
                        getChoferNombreBase(b),
                        "es",
                        { sensitivity: "base" }
                      )
                    )
                    .map((c) => (
                      <option key={c._id} value={c._id}>
                        {getChoferLabel(c)}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Desde</Form.Label>
                <Form.Control
                  type="date"
                  name="desde"
                  value={formValues.desde}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Hasta</Form.Label>
                <Form.Control
                  type="date"
                  name="hasta"
                  value={formValues.hasta}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>Fecha pago</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="fechapago"
                  value={formValues.fechapago}
                  onChange={handleChange}
                  disabled={guardando}
                />
              </Form.Group>
            </Col>

            <Col md={2}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="button"
                    onClick={cargarViajes}
                    disabled={cargandoViajes || guardando || !formValues.chofer}
                  >
                    {cargandoViajes ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Cargando
                      </>
                    ) : (
                      "Buscar viajes"
                    )}
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Medio de pago</Form.Label>
                <Form.Select
                  name="mediopago"
                  value={formValues.mediopago}
                  onChange={handleChange}
                  disabled={guardando}
                >
                  <option value="EFECTIVO">EFECTIVO</option>
                  <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                  <option value="CHEQUE">CHEQUE</option>
                  <option value="OTRO">OTRO</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={9}>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  type="text"
                  name="observaciones"
                  value={formValues.observaciones}
                  onChange={handleChange}
                  disabled={guardando}
                  placeholder="Ej: Liquidación fin de mes"
                />
              </Form.Group>
            </Col>
          </Row>

          {formValues.mediopago === "EFECTIVO" && (
            <Row className="mb-3">
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Caja</Form.Label>
                  <Form.Select
                    name="caja"
                    value={formValues.caja}
                    onChange={handleChange}
                    disabled={cargandoCajas || guardando}
                  >
                    <option value="">Seleccione caja</option>

                    {cajas.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.descripcion} - Saldo $ {money(c.saldoActual)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={5}>
                <div className="border rounded p-3 bg-light h-100">
                  <strong>Saldo disponible</strong>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color:
                        num(cajaSeleccionada?.saldoActual) >= montoTotalOperacion
                          ? "#198754"
                          : "#dc3545",
                    }}
                  >
                    $ {money(cajaSeleccionada?.saldoActual)}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {viajesCalculados.length > 0 && (
            <>
              <div className="border rounded p-3 mb-3 bg-light">
                <Row>
                  <Col md={2}>
                    <strong>Total km</strong>
                    <div>{kmFmt(resumen.km)}</div>
                  </Col>

                  <Col md={2}>
                    <strong>Valor viaje</strong>
                    <div>$ {money(resumen.valorviaje)}</div>
                  </Col>

                  <Col md={2}>
                    <strong>Gastos</strong>
                    <div>$ {money(resumen.gastos)}</div>
                  </Col>

                  <Col md={2}>
                    <strong>Saldo viajes</strong>
                    <div style={{ fontWeight: 700 }}>
                      $ {money(resumen.saldopendiente)}
                    </div>
                  </Col>

                  <Col md={2}>
                    <strong>Adelantos / ajustes</strong>
                    <div style={{ color: "#dc3545", fontWeight: 700 }}>
                      $ {money(diferenciaAdelantosExternos)}
                    </div>
                  </Col>

                  <Col md={2}>
                    <strong>Saldo real a pagar</strong>
                    <div style={{ color: "#198754", fontWeight: 700 }}>
                      $ {money(saldoRealCuenta)}
                    </div>
                  </Col>
                </Row>

                <Row className="mt-3 align-items-end">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Monto a pagar</Form.Label>
                      <Form.Control
                        type="text"
                        value={montoAPagar}
                        onChange={(e) => setMontoAPagar(e.target.value)}
                        disabled={guardando}
                        placeholder="Ej: 1915600"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3}>
                    <Button
                      variant="outline-success"
                      type="button"
                      onClick={aplicarSaldoReal}
                      disabled={guardando || cargandoViajes}
                      className="w-100"
                    >
                      Aplicar saldo real
                    </Button>
                  </Col>

                  <Col md={3}>
                    <strong>Seleccionado</strong>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      $ {money(montoTotalOperacion)}
                    </div>
                  </Col>

                  <Col md={3}>
                    <strong>Diferencia</strong>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color:
                          Math.abs(saldoRealCuenta - montoTotalOperacion) < 1
                            ? "#198754"
                            : "#dc3545",
                      }}
                    >
                      $ {money(saldoRealCuenta - montoTotalOperacion)}
                    </div>
                  </Col>
                </Row>
              </div>

              <div style={{ maxHeight: "420px", overflowY: "auto" }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Sel</th>
                      <th>Fecha</th>
                      <th>Viaje</th>
                      <th>Km</th>
                      <th>Valor km</th>
                      <th>Valor viaje</th>
                      <th>Viáticos</th>
                      <th>Gastos</th>
                      <th>Adelantos</th>
                      <th>Neto</th>
                      <th>Aplicado</th>
                      <th>Saldo</th>
                      <th style={{ minWidth: 140 }}>Aplicar ahora</th>
                    </tr>
                  </thead>

                  <tbody>
                    {viajesCalculados.map((v) => {
                      const checked = !!seleccion[v._id];

                      const montoAplicado = checked
                        ? num(seleccion[v._id]?.montoaplicado)
                        : 0;

                      const totalOtros = totalSeleccionadoEn(seleccion, v._id);

                      const disponibleParaEste = Math.max(
                        0,
                        montoObjetivoPago - totalOtros
                      );

                      const sinCupo = !checked && disponibleParaEste <= 0;

                      return (
                        <tr key={v._id}>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleToggleViaje(v)}
                              disabled={
                                guardando ||
                                (!checked && (v.saldopendiente <= 0 || sinCupo))
                              }
                            />
                          </td>

                          <td>{v.fecha}</td>
                          <td>{v.nroviaje}</td>
                          <td>{kmFmt(v.totalkm)}</td>
                          <td>$ {money(v.valorkm)}</td>
                          <td>$ {money(v.valorviaje)}</td>
                          <td>$ {money(v.totalviaticos)}</td>
                          <td>$ {money(v.totalgastos)}</td>
                          <td>$ {money(v.totaladelantos)}</td>
                          <td>$ {money(v.neto)}</td>
                          <td>$ {money(v.aplicadoprevio)}</td>

                          <td
                            style={{
                              color:
                                v.saldopendiente > 0 ? "#198754" : "#dc3545",
                              fontWeight: 700,
                            }}
                          >
                            $ {money(v.saldopendiente)}
                          </td>

                          <td>
                            <Form.Control
                              type="number"
                              min="0"
                              step="0.01"
                              max={Math.min(
                                v.saldopendiente,
                                disponibleParaEste + montoAplicado
                              )}
                              value={checked ? montoAplicado : ""}
                              onChange={(e) =>
                                handleMontoAplicado(v, e.target.value)
                              }
                              disabled={!checked || guardando}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {viajesCalculados.length === 0 && !cargandoViajes && (
            <Alert variant="secondary" className="mb-0">
              Seleccioná chofer y período, luego presioná{" "}
              <strong>Buscar viajes</strong>.
            </Alert>
          )}

          <div className="border rounded p-3 mt-3 bg-light">
            <Row>
              <Col md={4}>
                <strong>Viajes seleccionados:</strong>{" "}
                {aplicacionesSeleccionadas.length}
              </Col>

              <Col md={4}>
                <strong>Monto total a pagar:</strong> $ {money(montoTotalOperacion)}
              </Col>

              <Col md={4}>
                <strong>Fecha de pago:</strong> {formValues.fechapago}
              </Col>
            </Row>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={guardando}>
            Cancelar
          </Button>

          <Button variant="success" type="submit" disabled={guardando}>
            {guardando ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando
              </>
            ) : (
              "Guardar pago"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}